import {
 serverOrderStatus,
 serverOrderStatusContainer,
 serverOrderStatusGateway,
 serverOrderStatusWebMailNs
} from "constants/server";

import { IDockerOrderSelected } from "types/api/dockerApiInterface";
import { IGatewayOrderSelected } from "types/api/gatewayApiInterface";
import { IJobsData } from "types/api/hetznerJobs";
import { IJobQueueSelected } from "types/api/jobqueueApiInterface";
import { IOvhPriceRange } from "types/api/ovhApiInterface";
import { IServerTypes, IServerTypesApi } from "types/api/serversApiInterface";
import { IServerPermissionApi } from "types/api/userApiInterface";
import { linuxOs } from "types/global/server";

type serverCreationData = {
 os: number;
 location: number;
 serverSize: number;
 enableIpv4: boolean;
 enableBackup: boolean;
 enableContinuity: boolean;
 serverType: string;
};

type contaboCreationData = {
 os: number;
 location: number;
 serverSize: number;
 serverType: string;
};

type ovhCreationData = {
 serverType: string;
 serverCode: string;
 osOvh: string;
 locationOvh: string;
 enableContinuity: boolean;
};

export const divideServerTypes = (
 upgradesList: Array<IServerTypesApi>,
 type: string,
 rescale: boolean,
 serverDisk: number,
 serverType: string
): Array<IServerTypesApi> => {
 return upgradesList.filter((upgrade) => {
  if (type === "dedicated") {
   if (rescale)
    return (
     upgrade.name.includes("VFC") &&
     upgrade.disk >= serverDisk &&
     (!upgrade.name.includes(serverType.toUpperCase()) ||
      (upgrade.name.includes(serverType.toUpperCase()) && upgrade.disk > serverDisk))
    );
   else
    return (
     upgrade.name.includes("VFC") &&
     upgrade.disk >= serverDisk &&
     !upgrade.name.includes(serverType.toUpperCase())
    );
  } else {
   if (rescale)
    return (
     !upgrade.name.includes("VFC") &&
     upgrade.disk >= serverDisk &&
     (!upgrade.name.includes(serverType.toUpperCase()) ||
      (upgrade.name.includes(serverType.toUpperCase()) && upgrade.disk > serverDisk))
    );
   else
    return (
     !upgrade.name.includes("VFC") &&
     upgrade.disk >= serverDisk &&
     !upgrade.name.includes(serverType.toUpperCase())
    );
  }
 });
};

export const formatServerPrice = (
 typesList: Array<IServerTypes>,
 enabledIpv4: boolean,
 enabledBackup: boolean,
 enableContinuity: boolean,
 selectedType: number
): number => {
 const filteredList = typesList.find((element) => element.id === selectedType);
 return (
  Number(filteredList?.server_type_price) +
  (enableContinuity ? 5 : 0) +
  (enabledIpv4 ? Number(filteredList?.ipv4_total) : 0) +
  (enabledBackup ? Number(filteredList?.backup_total) : 0)
 );
};

export const checkServerOs = (os: string): "windows" | "linux" => {
 if (linuxOs.includes(os)) {
  return "linux";
 } else if (os === "windows") {
  return "windows";
 } else {
  return "linux";
 }
};

export const getJobInProgress = (
 jobs:
  | Array<IJobsData>
  | Array<IJobQueueSelected>
  | Array<IGatewayOrderSelected>
  | Array<IDockerOrderSelected>
): number => {
 let count = 0;
 for (let job of jobs) {
  if (job.status !== "success" && job.status !== "failed") {
   count++;
  }
 }
 return count;
};

export const renderOrderPercent = (status: string, type: string): number => {
 if (type.includes("webserver") || type.includes("nameserver") || type.includes("mailserver")) {
  const percent = 100 / (serverOrderStatus.length + serverOrderStatusWebMailNs.length);
  if (serverOrderStatus.includes(status)) {
   return Number(((serverOrderStatus.indexOf(status) + 1) * percent).toFixed(0));
  } else if (serverOrderStatusWebMailNs.includes(status)) {
   return Number(
    ((serverOrderStatus.indexOf(status) + 1 + serverOrderStatus.length) * percent).toFixed(0)
   );
  } else {
   return percent;
  }
 } else if (type.includes("container")) {
  const percent = 100 / (serverOrderStatus.length + serverOrderStatusContainer.length);
  if (serverOrderStatus.includes(status)) {
   return Number(((serverOrderStatus.indexOf(status) + 1) * percent).toFixed(0));
  } else if (serverOrderStatusContainer.includes(status)) {
   return Number(
    ((serverOrderStatus.indexOf(status) + 1 + serverOrderStatus.length) * percent).toFixed(0)
   );
  } else {
   return percent;
  }
 } else if (type.includes("gateway")) {
  const percent = 100 / (serverOrderStatus.length + serverOrderStatusGateway.length);
  if (serverOrderStatus.includes(status)) {
   return Number(((serverOrderStatus.indexOf(status) + 1) * percent).toFixed(0));
  } else if (serverOrderStatusContainer.includes(status)) {
   return Number(
    ((serverOrderStatus.indexOf(status) + 1 + serverOrderStatus.length) * percent).toFixed(0)
   );
  } else {
   return percent;
  }
 } else {
  const percent = 100 / serverOrderStatus.length;
  if (serverOrderStatus.includes(status)) {
   return Number(((serverOrderStatus.indexOf(status) + 1) * percent).toFixed(0));
  } else {
   return percent;
  }
 }
};

export const formatCountryCode = (country: string): string => {
 if (country === "JPN") {
  return "jp";
 } else if (country === "SIN") {
  return "sg";
 } else if (country === "US-west" || country === "US-central" || country === "US-east") {
  return "us";
 } else if (country === "AUS") {
  return "au";
 } else if (country === "IND") {
  return "in";
 } else if (country === "UK") {
  return "gb";
 } else {
  return country.toLocaleLowerCase();
 }
};

export const getProviderPermissions = (
 provider: string,
 permissions: Array<IServerPermissionApi>
): IServerPermissionApi | null => {
 switch (provider) {
  case "kx":
   return permissions.find((element) => element.provider === "hetzner") || null;
  case "ctb":
   return permissions.find((element) => element.provider === "ctb") || null;
  case "konsolex":
   return permissions.find((element) => element.provider === "hetzner") || null;
  case "ovh":
   return permissions.find((element) => element.provider === "ovh") || null;
  default:
   return null;
 }
};

export const searchOs = (os: string): string => {
 if (os.includes("debian")) {
  return `${process.env.PUBLIC_URL}/svg/os/Debian.svg`;
 } else if (os.includes("ubuntu")) {
  return `${process.env.PUBLIC_URL}/svg/os/Ubuntu.svg`;
 } else if (os.includes("centos")) {
  return `${process.env.PUBLIC_URL}/svg/os/CentOS.svg`;
 } else if (os.includes("windows")) {
  return `${process.env.PUBLIC_URL}/svg/os/Winzoz.svg`;
 } else {
  return `${process.env.PUBLIC_URL}/svg/os/Debian.svg`;
 }
};

export const checkHetznerSteps = (index: number, data: serverCreationData): boolean => {
 switch (index) {
  case 0:
   return data.serverType !== "" && data.os !== 0;
  case 1:
   return data.location !== 0;
  case 2:
   return data.serverSize !== 0;
  case 3:
   return data.enableIpv4;
  case 4:
   return data.enableBackup;
  case 5:
   return data.enableContinuity;
  default:
   return false;
 }
};

export const checkContaboSteps = (index: number, data: contaboCreationData): boolean => {
 switch (index) {
  case 0:
   return data.serverType !== "" && data.os !== 0;
  case 1:
   return data.location !== 0;
  case 2:
   return data.serverSize !== 0;
  default:
   return false;
 }
};

export const checkOvhSteps = (index: number, data: ovhCreationData): boolean => {
 switch (index) {
  case 0:
   return data.serverCode !== "";
  case 1:
   return data.serverType !== "" && data.osOvh !== "";
  case 2:
   return data.locationOvh !== "";
  case 3:
   return data.enableContinuity;
  default:
   return false;
 }
};

export const formatOvhPrice = (priceList: Array<IOvhPriceRange>, amount: number): number => {
 const findedPercentage =
  priceList.find((element) => Number(element.min) <= amount && Number(element.max) > amount)
   ?.percentage || 0;
 console.log(
  priceList.find((element) => Number(element.min) < amount && Number(element.max) > amount)
   ?.percentage
 );
 return amount + (amount * findedPercentage) / 100;
};

export const formatOvhCity = (country: string): string => {
 switch (country) {
  case "SBG":
   return "Strasbourg";
  case "BHS":
   return "Beauharnois";
  case "WAW":
   return "Warsaw";
  case "SYD":
   return "Sydney";
  case "SGP":
   return "Singapore";
  case "GRA":
   return "Gravelines";
  case "DE":
   return "Germany";
  case "UK":
   return "United Kingdom";
  case "YNM":
   return "Mumbai";
  case "PAR":
   return "Paris";
  case "RBX":
   return "Roubaix";
  case "LIM":
   return "Limburg";
  case "ERI":
   return "Erith";
  case "VIN":
   return "Vint Hill";
  case "HIL":
   return "Hillsboro";
  case "YYZ":
   return "Toronto";
  default:
   return "Not defined";
 }
};

export const formatOvhFlag = (country: string): string => {
 switch (country) {
  case "WAW":
   return "pl";
  case "DE":
   return "de";
  case "UK":
   return "gb";
  case "SYD":
   return "au";
  case "SGP":
   return "sg";
  case "GRA":
   return "fr";
  case "SBG":
   return "fr";
  case "BHS":
   return "ca";
  case "YNM":
   return "in";
  case "PAR":
   return "fr";
  case "RBX":
   return "fr";
  case "LIM":
   return "de";
  case "ERI":
   return "gb";
  case "VIN":
   return "us";
  case "HIL":
   return "us";
  case "YYZ":
   return "ca";
  default:
   return "";
 }
};
